import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { degradedSources, useSourceHealth } from "@/hooks/useSourceHealth";

/**
 * Persistent (session-scoped) banner that surfaces when one of the
 * backend's external data sources is degraded (circuit breaker OPEN or
 * HALF_OPEN).
 *
 * Why persistent vs. transient toast:
 *   When SENEAM or FAA NASR is down, the data the ops manager sees for
 *   the affected airports is stale by definition. That's the kind of
 *   silent-staleness this codebase explicitly refuses to ship — so the
 *   banner stays up the entire time the source is degraded, with a
 *   "Entendido" dismiss that hides it ONLY for the current tab session.
 *   A new tab will re-show it; the breaker recovering will hide it
 *   automatically.
 *
 * Why above the layout (not inside DashboardLayout-only):
 *   The banner sits at the very top so it shows on auth pages too —
 *   if SENEAM is down when the user is on the login screen, they
 *   deserve to know before drilling into an airport that won't load.
 */
export default function SourceDegradedBanner() {
  const { t } = useTranslation();
  const { data } = useSourceHealth();
  const [dismissed, setDismissed] = useState<string>(""); // signature of dismissed set

  const degraded = useMemo(() => degradedSources(data), [data]);
  // Sorted signature so dismissing aip_mx then having faa_nasr also
  // degrade re-shows the banner (the set changed).
  const signature = useMemo(() => [...degraded].sort().join(","), [degraded]);

  // If the breaker recovers (degraded = []), clear the dismiss so a
  // future failure shows the banner again without the user resetting.
  useEffect(() => {
    if (degraded.length === 0 && dismissed !== "") setDismissed("");
  }, [degraded.length, dismissed]);

  if (degraded.length === 0) return null;
  if (dismissed === signature) return null;

  // When exactly one source is degraded, surface ITS name in the
  // subtitle for precision. Multiple sources → generic plural copy.
  const subtitle =
    degraded.length === 1
      ? t("sourceDegraded.subtitleSingle", {
          source: t(`sourceDegraded.sources.${degraded[0]}`, {
            defaultValue: degraded[0],
          }),
        })
      : t("sourceDegraded.subtitle");

  return (
    <div
      role="alert"
      className="w-full bg-amber-500/15 border-b border-amber-500/40 px-3 py-2 flex items-center gap-2.5"
    >
      <AlertTriangle className="w-4 h-4 text-amber-300 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-xs font-semibold text-amber-100">{t("sourceDegraded.title")}</div>
        <div className="text-[11px] text-amber-200/80 truncate">{subtitle}</div>
      </div>
      <button
        onClick={() => setDismissed(signature)}
        aria-label={t("sourceDegraded.dismiss")}
        className="text-[11px] font-semibold text-amber-100 bg-amber-500/25 hover:bg-amber-500/35 border border-amber-500/40 rounded-lg px-2 py-1 flex-shrink-0 flex items-center gap-1"
      >
        <X className="w-3 h-3" />
        {t("sourceDegraded.dismiss")}
      </button>
    </div>
  );
}
